// Display only: some supplier ArcGIS exports use an ESRI GUID as ProductionPlace, which must never be rendered.
import i18n from '@/i18n'
import { TractTypeCategory } from '@/utils/Enumerations.js'

const GUID_PATTERN = /^\{?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\}?$/i

function isGuidLike (value) {
  return typeof value === 'string' && GUID_PATTERN.test(value.trim())
}

// CSV point-mode tracts carry a synthetic `point:<lon>,<lat>` parcelId, not a real ReportAll robust_id.
export function isSyntheticParcelId (parcelId) {
  return typeof parcelId === 'string' && parcelId.startsWith('point:')
}

// tractType is the numeric TractTypeCategory value; composites generated before the enum typing carried the name.
export function tractTypeLabel (tractType) {
  if (tractType === null || tractType === undefined) return null
  return TractTypeCategory.forInt(tractType)?.name ??
    (typeof tractType === 'string' ? tractType : null)
}

// Human place data occasionally riding along in the supplier's original properties (kept in sourceProperties).
function placeFromSourceProperties (sourceProperties) {
  let parsed = sourceProperties
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed)
    } catch (e) {
      return null
    }
  }
  if (!parsed || typeof parsed !== 'object') return null
  const county = parsed.county_name ?? parsed.County ?? parsed.county ?? parsed.CountyName
  const state = parsed.state_abbr ?? parsed.State ?? parsed.state ?? parsed.StateAbbr
  const parts = [county, state].filter(Boolean)
  return parts.length ? parts.join(', ') : null
}

// Cheap centroid: average of the outer ring(s)' vertices. Good enough for a display label, not for geometry math.
function centroidOf (geometry) {
  const rings = geometry?.type === 'Polygon'
    ? [geometry.coordinates?.[0]]
    : geometry?.type === 'MultiPolygon'
      ? (geometry.coordinates ?? []).map(polygon => polygon?.[0])
      : geometry?.type === 'Point'
        ? [[geometry.coordinates]]
        : null
  if (!rings) return null

  const points = rings.filter(Boolean).flat().filter(p => Array.isArray(p) && p.length >= 2)
  if (!points.length) return null

  let sumLon = 0
  let sumLat = 0
  for (const [lon, lat] of points) {
    sumLon += lon
    sumLat += lat
  }
  return { lat: sumLat / points.length, lon: sumLon / points.length }
}

/**
 * Resolves the best display name for an EUDR GeoJSON feature. Never returns a GUID.
 * @param {object} feature a GeoJSON feature
 * @returns {string}
 */
export function featureDisplayName (feature) {
  const props = feature?.properties ?? {}

  if (props.productionPlace && !isGuidLike(props.productionPlace)) return props.productionPlace

  if (props.parcelId && !isGuidLike(props.parcelId) && !isSyntheticParcelId(props.parcelId)) {
    return `${i18n.t('parcelNumber')} ${props.parcelId}`
  }

  const parts = []
  if (props.producerName) parts.push(props.producerName)

  const place = placeFromSourceProperties(props.sourceProperties)
  if (place) {
    parts.push(place)
  } else {
    const centroid = centroidOf(feature?.geometry)
    if (centroid) parts.push(`${centroid.lat.toFixed(4)}, ${centroid.lon.toFixed(4)}`)
  }

  const area = typeof props.area === 'number' ? props.area : Number(props.area)
  if (Number.isFinite(area)) parts.push(`${area} ha`)

  return parts.length ? parts.join(' · ') : i18n.t('unknown')
}
